import React from "react"

import {Card} from "@components/index"
import clsx from "clsx"

type ContentProps = {
    title?: string
    content?: (string | React.ReactNode)[],
    imagePath?: string
}

type SectionProps = {
    bgColor: string,
    title?: string,
    center?: ContentProps,
    leftSide: ContentProps,
    rightSide?: ContentProps
}

function RenderContent({title, content = [], imagePath}: ContentProps) {

    return <>
        {title &&
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{title}</h2>}
        {imagePath && <img className="img-fluid d-block mx-auto w-full" src={imagePath}
                           alt=""/>}
        {content.map((x, i) =>
            <p key={i} className="mt-6 text-xl leading-8 text-gray-700">
                {x}
            </p>)}
    </>
}

function Section({bgColor, leftSide, center, rightSide}: SectionProps) {

    return <div className="grid grid-cols-2 prose max-w-full" style={{background: bgColor}}>
        {center &&
          <div className={clsx("col-span-full px-8 pt-8")}>
            <RenderContent {...center} />
          </div>
        }
        <div className={clsx("col-span-full p-8", rightSide && "sm:col-span-1")}>
            <RenderContent {...leftSide} />
        </div>
        {rightSide &&
          <div className="col-span-full sm:col-span-1 p-8">
            <RenderContent {...rightSide} />
          </div>}
    </div>
}

export default function HowToPlay() {


    return <Card title="How to Play" bodyClassName="p-0">

        <Section bgColor="#fbfbf5"
                 leftSide={{
                     title: "Login",
                     content: ["You have to log in to make your labels matter. Create an account so that your points will be saved, and so that you will always see fresh stories — no repeats."]
                 }}
                 rightSide={{
                     imagePath: "/img/login.png"
                 }}/>

        <Section
            bgColor="#B4B980"
            leftSide={{
                title: "Choose Your Game",
                content: [
                    "There are two different ways to play. If you choose Mixed Mode, you will see one story at a time, and then you’ll be asked if there are many different types of legal issues present — a family issue, a criminal issue, a work issue, an immigration issue, etc. For each question you answer, you’ll get points!",
                    "Or, you can choose a specific Legal Issue area. You will see many different stories, and you’ll be asked if that specific Issue is present. You can deep dive into a specific area of law that you care about — whether it’s criminal law, immigration, consumer and debt issues, or otherwise."
                ]
            }}
            rightSide={{
                imagePath: "/img/offerings.png"
            }}/>

        <Section
            bgColor="#B5D1BB"
            leftSide={{
                title: "Play",
                content: [
                    "Once you choose your game type, then you can play! You’ll see a person’s post title and their story. Then you answer the question about whether an issue is present or not. Click the Green Check if you think this legal issue is present. For some posts, it might not be totally clear — but you can make the judgment call about whether you think this legal issue might be there.",
                    "Click the Red X if this legal issue is definitely not present. Click the Grey Arrow button if you’re not sure. This is the Pass Button, if it’s not clear whether the issue is present or not.",
                    "Remember — the same story can have multiple Green Checks, because it might have multiple issues. If you think a legal issue is present, we encourage you to click the Green Button.",
                    "After your game is over, you’ll see how many points you won, and where you are on the leaderboard. Keep playing to move up the ranks!"
                ]
            }}
            rightSide={{
                imagePath: "/img/play.png"
            }}
        />

        <Section
            bgColor="#97B6A8"
            leftSide={{
                title: "Changing Your Mind",
                content: [
                    "If you think that you clicked the wrong button — and you are still playing that round of the game — you can scroll down under the buttons. There is the History section, where you can change your response.",
                    "To change, click on the question response you want to change. It will drop down to expand. Then click the correct response that you want to register — the green checkmark, the red x, or the gray pass."
                ]
            }}
            rightSide={{
                imagePath: "/img/history.png"
            }}
        />

        <Section
            bgColor="#18bc9c"
            center={{
                title: "Extras",
                content: ["Once you’ve gotten used to playing, there are a few shortcuts and extra features to know about."]
            }}
            leftSide={{
                content: [
                    <><strong>Keyboard shortcuts:</strong> on your desktop, play quicker by using the Z key for the
                        green checkmark, the X key for the red x, and the C key for the gray pass. You can play quicker
                        with the keys!</>,
                    <><strong>Question explanation:</strong> if you’re not sure about what a legal help issue (like
                        family law, or accidents and injuries) refers to — you can see exactly what it means. Click on
                        the gray question mark on the right side of the question box. It will give you an explanation of
                        what topics it covers.</>
                ]
            }}
            rightSide={{
                content: [
                    <>
                        <strong>See the post in detail:</strong> if you want to see the original post — where it was on
                        Reddit, and how people replied to it, click the original button at the top left of the question
                        box — right above the title. This will take you to another browser window, where you can see the
                        post on Reddit.
                    </>,
                    <>
                        <strong>Flag inappropriate content:</strong> If you see a post that is vulgar, irrelevant, or
                        otherwise inappropriate for legal help issue-spotting, you can flag it with the dark gray flag
                        at the bottom right corner of the story box. We can then make sure that other players don’t see
                        this story.
                    </>
                ]
            }}
        />
        <Section
            bgColor="#319682"
            leftSide={{
                title: "How to Answer",
                content: [
                    "When you’re reading the stories, it’s possible that there are several different types of legal issues present in the same story. Just because there is a work legal issue present, doesn’t mean there aren’t other types of legal issues present too — like immigration, criminal law, or benefits. If you see multiple issues present in the same story, you should check yes to all issues that you see present."
                ]
            }}
        />

    </Card>
}